import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import {BuildingImage} from './BuildingImage';
import { presentComplianceTruth, type ComplianceTruthProjection } from '../presentation/compliance-truth';
import { MAP_TILE_SIZE, mapTiles, type GeoPoint } from './geo-map';
import { StatusBadge } from './StatusBadge';

export type MappedBuilding = {
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  residential_units: number;
  commercial_units: number;
  declared_residential_units: number | null;
  declared_commercial_units: number | null;
  active_work: number | null;
  primary_image_document_id?: string | null;
  primary_image_version_id?: string | null;
  address?: string | null;
  compliance_truth?: ComplianceTruthProjection | undefined;
};

const mapTileTemplate = import.meta.env.VITE_MAP_TILE_URL_TEMPLATE || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

function MapTileBackdrop({ centre }: { centre: GeoPoint }) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 700, height: 390 });
  const [tilesFailed,setTilesFailed]=useState(false),[tilesRevision,setTilesRevision]=useState(0);
  const zoom = 17;
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setSize({ width: element.clientWidth || 700, height: element.clientHeight || 390 });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const tiles = useMemo(
    () => mapTiles(centre, size.width, size.height, zoom),
    [centre.latitude, centre.longitude, size],
  );
  return <>
    <div ref={ref} className="location-map-tiles" aria-hidden="true">
      {tiles.map(tile => <img
        key={`${tile.key}:${tilesRevision}`}
        src={mapTileTemplate.replace('{z}', String(zoom)).replace('{x}', String(tile.urlX)).replace('{y}', String(tile.y))}
        style={{ left: tile.left, top: tile.top, width: MAP_TILE_SIZE, height: MAP_TILE_SIZE }}
        alt=""
        draggable={false}
        onError={()=>setTilesFailed(true)}
      />)}
    </div>
    {tilesFailed&&<div className="map-tile-error" role="status">Map background unavailable. Recorded coordinates: {centre.latitude}, {centre.longitude}. <button type="button" className="secondary" onClick={()=>{setTilesFailed(false);setTilesRevision(value=>value+1);}}>Retry map</button></div>}
    <a className="location-map-attribution" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">© OpenStreetMap contributors</a>
  </>;
}

export function BuildingLocationMap({ buildings, single = false }: { buildings: MappedBuilding[]; single?: boolean }) {
  const located = buildings.filter(building => Number.isFinite(building.latitude) && Number.isFinite(building.longitude));
  const missing = buildings.filter(building => !Number.isFinite(building.latitude) || !Number.isFinite(building.longitude));
  const bounds = useMemo(() => {
    const latitudes = located.map(building => building.latitude!);
    const longitudes = located.map(building => building.longitude!);
    return {
      minLat: Math.min(...latitudes),
      maxLat: Math.max(...latitudes),
      minLng: Math.min(...longitudes),
      maxLng: Math.max(...longitudes),
    };
  }, [located]);
  const [position, setPosition] = useState<string>();

  return <div className="shared-building-location">
    {located.length ? <div className="portfolio-map" role="group" aria-label={single ? 'Building location' : 'Portfolio building map'}>
      {single
        ? <MapTileBackdrop key={`${located[0]!.latitude}:${located[0]!.longitude}`} centre={{ latitude: located[0]!.latitude!, longitude: located[0]!.longitude! }} />
        : <div className="map-grid" aria-hidden="true" />}
      {located.map((building, index) => {
        const x = located.length === 1 ? 50 : 8 + 84 * ((building.longitude! - bounds.minLng) / (bounds.maxLng - bounds.minLng || 1));
        const y = located.length === 1 ? 50 : 8 + 84 * (1 - (building.latitude! - bounds.minLat) / (bounds.maxLat - bounds.minLat || 1));
        const compliance = presentComplianceTruth(building.compliance_truth?.state);
        return <button
          key={building.id}
          className="map-pin"
          data-compliance-state={compliance.state}
          style={{ left: `${x}%`, top: `${y}%` }}
          aria-label={`${building.name}, compliance status: ${compliance.label}, ${building.residential_units} recorded residential units, ${building.commercial_units} commercial units`}
          aria-expanded={position === building.id}
          onClick={() => setPosition(building.id)}
          onFocus={() => setPosition(building.id)}
          onKeyDown={event => { if (event.key === 'Escape') setPosition(undefined); }}
        ><span>{index + 1}</span></button>;
      })}
      {located.map(building => {
        if (position !== building.id) return null;
        const compliance = presentComplianceTruth(building.compliance_truth?.state);
        return <article key={building.id} className="map-popover">
          <BuildingImage building={building} compact />
          <div>
            <strong>{building.name}</strong>
            <span>{building.residential_units} residential · {building.commercial_units} commercial</span>
            <StatusBadge text={compliance.label} tone={compliance.tone} />
            {building.active_work !== null && building.active_work > 0 && <small>{building.active_work} active work record{building.active_work === 1 ? '' : 's'}</small>}
            <Link to={`/buildings/${building.id}`}>Open building</Link>
          </div>
        </article>;
      })}
    </div> : <div className="location-unavailable">
      <strong>Location not yet available</strong>
      <p>{buildings[0]?.address ?? 'A map position has not been confirmed.'}</p>
      {single && <StatusBadge
        text={presentComplianceTruth(buildings[0]?.compliance_truth?.state).label}
        tone={presentComplianceTruth(buildings[0]?.compliance_truth?.state).tone}
      />}
    </div>}
    {!single && missing.length > 0 && <section className="location-needed">
      <h2>Location needed</h2>
      <p>These buildings stay off the map until an authorised staff member confirms their position.</p>
      <div>{missing.map(building => {
        const compliance = presentComplianceTruth(building.compliance_truth?.state);
        return <Link key={building.id} to={`/buildings/${building.id}?confirmLocation=1`}>
          {building.name}<StatusBadge text={compliance.label} tone={compliance.tone} />
        </Link>;
      })}</div>
    </section>}
  </div>;
}
